import { el, clear } from '../lib/dom.js';
import { loadSnapshot } from '../data/snapshot.js';
import {
  versionGroupsByGeneration,
  dexesForVersionGroup,
  prettify,
} from '../data/pokedex-resolver.js';
import { settings } from '../store/settings.js';
import { gameColors } from '../data/game-colors.js';
import { dragToClose } from '../lib/sheet-drag.js';

let openInstance = null;

/**
 * A summonable overlay for picking the game (version group) and, for games
 * with more than one regional dex, the active sub-dex. Slides up from the
 * bottom; dismiss with the backdrop, the ✕, or Escape. Selecting writes to
 * `settings`, which the feed reacts to — no navigation.
 */
export async function openGameSheet() {
  if (openInstance) return;
  const snap = await loadSnapshot();

  const backdrop = el('div', {
    class: 'sheet-backdrop',
    onclick: (e) => {
      if (e.target === backdrop) close();
    },
  });
  const panel = el('div', {
    class: 'gsheet',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'Choose your game',
  });

  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };

  function close() {
    document.removeEventListener('keydown', onKey);
    backdrop.classList.remove('is-open');
    setTimeout(() => {
      backdrop.remove();
      openInstance = null;
    }, 220);
  }

  const subdex = el('div', { class: 'gsheet__subdex' });
  const list = el('div', { class: 'gsheet__list' });

  function renderSubdex() {
    const st = settings.get();
    const dexes = dexesForVersionGroup(snap, st.versionGroup);
    clear(subdex);
    if (dexes.length <= 1) return;
    subdex.append(el('span', { class: 'gsheet__label' }, 'Pokédex'));
    for (const dex of dexes) {
      const active = (st.pokedex || dexes[0].key) === dex.key;
      subdex.append(
        el(
          'button',
          {
            class: `feed-chip${active ? ' is-active' : ''}`,
            type: 'button',
            onclick: () => {
              settings.set({ pokedex: dex.key });
              close();
            },
          },
          prettify(dex.name || dex.key),
        ),
      );
    }
  }

  function renderGames() {
    const st = settings.get();
    clear(list);

    list.append(
      el(
        'button',
        {
          class: `gamecard gamecard--all${st.versionGroup === 'all' ? ' is-active' : ''}`,
          type: 'button',
          onclick: () => {
            settings.set({ versionGroup: 'all', pokedex: null });
            close();
          },
        },
        el('span', { class: 'gamecard__name' }, 'All games'),
        el('span', { class: 'gamecard__meta' }, 'National Dex · newest data, no generation limits'),
      ),
    );

    for (const { generation, versionGroups } of versionGroupsByGeneration(snap)) {
      list.append(el('h3', { class: 'gsheet__gen' }, `Gen ${generation.id} · ${generation.name}`));
      const grid = el('div', { class: 'gsheet__grid' });
      for (const vg of versionGroups) {
        const active = vg.key === st.versionGroup;
        const dexCount = dexesForVersionGroup(snap, vg.key).length;
        const [c1, c2] = gameColors(vg.key, generation.id);
        grid.append(
          el(
            'button',
            {
              class: `gamecard gamecard--cover${active ? ' is-active' : ''}`,
              type: 'button',
              style: `--g1:${c1};--g2:${c2}`,
              onclick: () => {
                settings.set({ versionGroup: vg.key, pokedex: null });
                if (dexesForVersionGroup(snap, vg.key).length <= 1) {
                  close();
                } else {
                  renderGames();
                  renderSubdex();
                }
              },
            },
            el('span', { class: 'gamecard__name' }, prettify(vg.name || vg.key)),
            el(
              'span',
              { class: 'gamecard__foot' },
              el('span', { class: 'gamecard__meta' }, vg.versions.map(prettify).join(' / ')),
              el(
                'span',
                { class: 'gamecard__dex' },
                `${dexCount} ${dexCount === 1 ? 'dex' : 'dexes'}`,
              ),
            ),
          ),
        );
      }
      list.append(grid);
    }
  }

  const head = el(
    'div',
    { class: 'gsheet__head' },
    el('h2', {}, 'Choose your game'),
    el(
      'button',
      { class: 'gsheet__close', type: 'button', 'aria-label': 'Close', onclick: close },
      '✕',
    ),
  );
  dragToClose(panel, head, close);
  panel.append(head, subdex, list);
  backdrop.append(panel);
  document.body.append(backdrop);
  document.addEventListener('keydown', onKey);
  openInstance = backdrop;

  renderGames();
  renderSubdex();
  requestAnimationFrame(() => backdrop.classList.add('is-open'));
}
